/**
 * Input sanitization utilities to prevent XSS and injection attacks
 * All user inputs should be sanitized before being used or sent to external services
 */

/**
 * Sanitizes a string by removing potentially dangerous characters
 * @param {string} input - The input string to sanitize
 * @param {number} maxLength - Maximum allowed length (default: 1000)
 * @returns {string} - Sanitized string
 */
export const sanitizeString = (input, maxLength = 1000) => {
  if (typeof input !== 'string') {
    return '';
  }

  // Trim whitespace
  let sanitized = input.trim();

  // Limit length to prevent DoS attacks
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  // Remove potentially dangerous characters but allow normal text
  // This removes script tags, event handlers, and other XSS vectors
  sanitized = sanitized
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    // Remove javascript: payloads like `javascript:alert(1)` (strip until whitespace)
    .replace(/javascript:[^\s]+/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/&#x?[0-9a-f]+;/gi, '');

  return sanitized;
};

/**
 * Validates and sanitizes an email address
 * @param {string} email - Email address to validate
 * @returns {string|null} - Sanitized email or null if invalid
 */
export const sanitizeEmail = (email) => {
  if (typeof email !== 'string') {
    return null;
  }

  const trimmed = email.trim().toLowerCase();
  
  // Basic email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!emailRegex.test(trimmed)) {
    return null;
  }

  // Additional length check
  if (trimmed.length > 254) {
    return null;
  }

  return trimmed;
};

/**
 * Validates and sanitizes a phone number
 * @param {string} phone - Phone number to validate
 * @returns {string|null} - Sanitized phone or null if invalid
 */
export const sanitizePhone = (phone) => {
  if (typeof phone !== 'string') {
    return null;
  }

  // Remove all non-digit characters except +, -, spaces, and parentheses
  const cleaned = phone.replace(/[^\d+\-() ]/g, '');
  
  // Check if it's a reasonable phone number length (between 10 and 20 characters)
  const digitsOnly = cleaned.replace(/\D/g, '');
  
  if (digitsOnly.length < 10 || digitsOnly.length > 20) {
    return null;
  }

  return cleaned.trim();
};

/**
 * Sanitizes form data object
 * @param {Object} formData - Form data object
 * @returns {Object} - Sanitized form data
 */
export const sanitizeFormData = (formData) => {
  if (!formData || typeof formData !== 'object') {
    return {};
  }

  const sanitized = {};

  // Sanitize each field based on its type
  if (formData.name) {
    sanitized.name = sanitizeString(formData.name, 100);
  }

  if (formData.email) {
    sanitized.email = sanitizeEmail(formData.email);
  }

  if (formData.phone) {
    sanitized.phone = sanitizePhone(formData.phone);
  }

  if (formData.company) {
    sanitized.company = sanitizeString(formData.company, 200);
  }

  if (formData.message) {
    sanitized.message = sanitizeString(formData.message, 5000);
  }

  if (formData.partdesc) {
    sanitized.partdesc = sanitizeString(formData.partdesc, 5000);
  }

  return sanitized;
};

/**
 * Validates required fields in form data
 * @param {Object} formData - Form data to validate
 * @param {Array<string>} requiredFields - Array of required field names
 * @returns {Object} - { isValid: boolean, errors: Array<string> }
 */
export const validateFormData = (formData, requiredFields = []) => {
  const errors = [];

  requiredFields.forEach(field => {
    if (!formData[field] || formData[field].trim() === '') {
      errors.push(`${field} is required`);
    }
  });

  // Validate email format if email is present
  if (formData.email && !sanitizeEmail(formData.email)) {
    errors.push('Invalid email format');
  }

  // Validate phone format if phone is present
  if (formData.phone && !sanitizePhone(formData.phone)) {
    errors.push('Invalid phone number format');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};
